/**
 * D-EZ-109 — THE WIRE: THE WEEK'S NEWS BETWEEN RACE N AND RACE N+1.
 *
 * One function, `buildWire(league, season, race)`, and it is pure: the same
 * league in gives the same Wire out, every time, so a back Wire is never
 * stored — it is printed again from the history whenever someone picks it up.
 * No model is called (the written Wire costs nothing and may appear on its
 * own); no engine die is drawn (choices are FNV hashes of stable ids).
 */

import { shortTrack, trackInProse } from '../paper/fallbackPaper.ts';
import { stableHash } from '../story/hometown.ts';
import { garageQuotes, type WireQuote } from './garage.ts';
import { num, say, type WireLine } from './line.ts';
import { fieldLine, oddsFor, type OddsEntry } from './odds.ts';
import { powerWeeks, type PowerRow } from './power.ts';
import { rivalryWatch, type WireRivalry } from './rivals.ts';
import { sponsorDesk } from './sponsors.ts';
import type { WireLeague } from './source.ts';
import { weekContext, type WeekContext } from './week.ts';
import { outAfterRace } from './injuries.ts';

export interface WireOdds {
  driverId: string;
  name: string;
  line: string;
  chance: number;
  note: WireLine | null;
}

export interface Wire {
  key: string;
  seasonNumber: number;
  /** The race the week follows. */
  afterRace: number;
  raceName: string;
  track: string;
  next: {
    raceNumber: number;
    raceName: string;
    track: string;
    turns: number;
    doublePoints: boolean;
    round: string;
  } | null;
  headline: WireLine;
  standfirst: WireLine | null;
  quotes: WireQuote[];
  power: PowerRow[];
  odds: { entries: WireOdds[]; field: { line: string; chance: number } | null; favourite: string | null } | null;
  injuries: WireLine[];
  sponsors: WireLine[];
  rivalries: WireRivalry[];
}

/** How many names the line prints before "the field". */
export const ODDS_SHOWN = 8;

// ---------------------------------------------------------------------------
// The injury report, in words
// ---------------------------------------------------------------------------

const INJ = {
  died: ['The league mourns {name}, lost at {track}.'],
  hurtSeason: ['{name} is hurt and out for the rest of the season.', 'Season over for {name}: he is hurt and will not race again this year.'],
  hurtN: ['{name} is hurt and misses {n} {n|race|races}.', '{name} will sit out {n} {n|race|races} with his injury.'],
  hurtNBack: ['{name} is hurt and misses {n} {n|race|races}; back for {back}.', '{name} sits out {n} {n|race|races} hurt — due back at {back}.'],
  suspendedSeason: ['{name} is suspended for the rest of the season.'],
  suspendedN: ['{name} is suspended for {n} {n|race|races}.', 'Parked: {name} sits out {n} {n|race|races}.'],
  between: ['{name} quit his ride and is between seats — {n} {n|week|weeks} before he signs elsewhere.'],
  back: ['{name} is cleared to race again at {nextTrack}.', '{name} returns at {nextTrack}.', 'Welcome back: {name} is on the entry list for {nextTrack}.'],
} as const;

function injuryLines(w: WeekContext): WireLine[] {
  const out: WireLine[] = [];
  const nextTrack = w.next ? shortTrack(w.next.trackName) : null;
  for (const d of w.out.died) {
    const l = say('inj-died', INJ.died, { name: d.name, track: w.trackShort }, { key: `${w.key}|${d.driverId}`, driverIds: [d.driverId] });
    if (l) out.push(l);
  }
  // This week's news gets a sentence each.
  for (const o of w.out.out.filter((x) => x.isNew)) {
    const name = w.nameOf(o.driverId);
    const key = `${w.seasonKey}|${o.driverId}`;
    const ids = [o.driverId];
    const n = o.racesLeft === 'season' ? null : num(o.racesLeft, 'word');
    const backRace = typeof o.racesLeft === 'number' ? w.schedule.find((r) => r.raceNumber === w.raceNumber + 1 + (o.racesLeft as number)) : undefined;
    let l: WireLine | null = null;
    if (o.kind === 'injured') {
      l =
        o.racesLeft === 'season'
          ? say('inj-hurt-season', INJ.hurtSeason, { name }, { key, driverIds: ids })
          : backRace
            ? say('inj-hurt-back', INJ.hurtNBack, { name, n, back: shortTrack(backRace.trackName) }, { key, driverIds: ids })
            : say('inj-hurt', INJ.hurtN, { name, n }, { key, driverIds: ids });
    } else if (o.kind === 'suspended') {
      l = o.racesLeft === 'season' ? say('inj-susp-season', INJ.suspendedSeason, { name }, { key, driverIds: ids }) : say('inj-susp', INJ.suspendedN, { name, n }, { key, driverIds: ids });
    } else {
      l = say('inj-between', INJ.between, { name, n }, { key, driverIds: ids });
    }
    if (l) out.push(l);
  }
  for (const id of w.out.back) {
    const l = say('inj-back', INJ.back, { name: w.nameOf(id), nextTrack }, { key: `${w.seasonKey}|${id}`, occurrence: w.raceNumber, driverIds: [id] });
    if (l) out.push(l);
  }
  // Everyone already on the list goes in one roll call, the way a weekly
  // prints its injury report — not a fresh sentence for the same man every week.
  const carried = w.out.out.filter((x) => !x.isNew);
  if (carried.length > 0) {
    const parts: string[] = [];
    const slots: Record<string, ReturnType<typeof num> | string> = {};
    carried.forEach((o, i) => {
      slots[`name${i}`] = w.nameOf(o.driverId);
      if (o.racesLeft === 'season') parts.push(`{name${i}} (${o.kind === 'suspended' ? 'suspended, ' : ''}out for the season)`);
      else {
        slots[`n${i}`] = num(o.racesLeft, 'word');
        parts.push(
          o.kind === 'suspended'
            ? `{name${i}} (suspended, {n${i}} more {n${i}|race|races})`
            : o.kind === 'between-rides'
              ? `{name${i}} (between rides, {n${i}} more {n${i}|week|weeks})`
              : `{name${i}} ({n${i}} more {n${i}|race|races})`,
        );
      }
    });
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    const l = say('inj-roll', [`Still out: ${list}.`, `Also on the sidelines: ${list}.`], slots, { key: w.seasonKey, occurrence: w.raceNumber, driverIds: carried.map((o) => o.driverId) });
    if (l) out.push(l);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The line
// ---------------------------------------------------------------------------

const NOTE = {
  trackWins: ['Won here {n} {n|time|times}.', '{n} {n|win|wins} at this track.', 'Has won here {n} {n|time|times} before.', 'Knows the way to victory lane here: {n} {n|win|wins}.'],
  trackAvg: ['Averages {avg} here.', 'Average finish of {avg} at this track.', 'Runs well here: {avg} on average.', 'A {avg} average at this place.'],
  trackBest: ['Best here: {best}.', 'Has been as good as {best} at this track.', 'Ran {best} here once.', 'Best finish at this track: {best}.'],
  leader: ['Points leader.', 'Leads the standings.', 'The man at the top of the table.', 'First in points.'],
  seasonWins: ['{n} wins this year, {rank} in points.', '{n} wins on the season.', 'A {n}-win year so far; {rank} in the standings.', '{n} trips to victory lane this year.'],
  lastThree: ['Last three: {a}, {b}, {c}.', 'Finished {a}, {b}, {c} in his last three.', 'Form: {a}, {b}, {c}.', 'Recent finishes {a}, {b}, {c}.'],
  seasonAvg: ['Averaging {avg} this year.', 'A {avg} average finish this season.', 'Season average: {avg}.', '{avg} average finish on the year.'],
  points: ['{rank} in points, averaging {avg}.', 'Sits {rank} in the standings; {avg} average finish.', '{rank} in points with a {avg} average.', 'Averaging {avg}, {rank} in the table.'],
  hot: ['Won last week.', 'Fresh off a win.', 'Coming off the win.', 'The last man to win.'],
  firstHere: ['A first start here — {avg} average finish elsewhere this year.', 'Never raced here in this league; averaging {avg} elsewhere.'],
} as const;

function oddsNote(w: WeekContext, e: OddsEntry): WireLine | null {
  const key = `${w.seasonKey}|${e.driverId}|odds`;
  const ids = [e.driverId];
  const cands: { kind: string; t: readonly string[]; slots: Record<string, ReturnType<typeof num> | string | null>; s: number }[] = [];
  const stat = w.table.byDriver[e.driverId];
  if (w.run.race.winnerId === e.driverId) cands.push({ kind: 'odds-hot', t: NOTE.hot, slots: {}, s: 90 });
  if (e.trackWins > 0) cands.push({ kind: 'odds-track-wins', t: NOTE.trackWins, slots: { n: num(e.trackWins, 'word') }, s: 80 + e.trackWins });
  if (w.table.drivers[0]?.driverId === e.driverId) cands.push({ kind: 'odds-leader', t: NOTE.leader, slots: {}, s: 70 });
  if (e.wins >= 2) cands.push({ kind: 'odds-season-wins', t: NOTE.seasonWins, slots: { n: num(e.wins, 'word'), rank: stat ? num(stat.rank, 'ord') : null }, s: 60 + e.wins });
  if (e.trackAvg !== null && e.trackStarts >= 2 && e.trackAvg <= 12) cands.push({ kind: 'odds-track-avg', t: NOTE.trackAvg, slots: { avg: num(e.trackAvg, 'dec1') }, s: 55 });
  if (e.trackBest !== null && e.trackBest <= 5 && e.trackWins === 0) cands.push({ kind: 'odds-track-best', t: NOTE.trackBest, slots: { best: num(e.trackBest, 'ord') }, s: 50 });
  if (e.seasonAvg !== null && e.starts >= 4 && e.seasonAvg <= 14) cands.push({ kind: 'odds-season-avg', t: NOTE.seasonAvg, slots: { avg: num(e.seasonAvg, 'dec1') }, s: 45 });
  if (e.lastThree.length === 3) cands.push({ kind: 'odds-last-three', t: NOTE.lastThree, slots: { a: num(e.lastThree[0], 'ord'), b: num(e.lastThree[1], 'ord'), c: num(e.lastThree[2], 'ord') }, s: 40 });
  if (stat && stat.rank <= 10 && w.table.drivers[0]?.driverId !== e.driverId && e.seasonAvg !== null) cands.push({ kind: 'odds-points', t: NOTE.points, slots: { rank: num(stat.rank, 'ord'), avg: num(e.seasonAvg, 'dec1') }, s: 35 });
  // Only news when the league has been here before and he has not.
  if (e.trackStarts === 0 && e.trackRaces > 0 && e.seasonAvg !== null && e.starts >= 3) cands.push({ kind: 'odds-first-here', t: NOTE.firstHere, slots: { avg: num(e.seasonAvg, 'dec1') }, s: 30 });
  if (cands.length === 0) return null;
  cands.sort((a, b) => b.s - a.s);
  // The top three take turns, so a man near the top of the line all season
  // does not read the same note every week.
  const top = cands.slice(0, 3);
  const turn = w.raceNumber + (stableHash(key) % 3);
  const c = top[turn % top.length];
  return say(c.kind, c.t, c.slots, { key, occurrence: Math.floor(turn / top.length), driverIds: ids });
}

// ---------------------------------------------------------------------------
// The front page
// ---------------------------------------------------------------------------

const HEAD = {
  won: ['{w} wins at {t}', '{w} takes {t}', '{t} goes to {w}', '{w} on top at {t}', '{w} conquers {t}'],
  first: ['{w} breaks through at {t}', 'First career win for {w}', '{w} wins for the first time, at {t}'],
  deep: ['{w} comes from {start} to win at {t}', 'From {start} to victory lane: {w} at {t}'],
  streak: ['{w} makes it {n} straight', '{n} in a row for {w}', '{w} can’t be stopped: {n} straight'],
  // Second clauses — the week's other story.
  leadChange: ['{l} takes over the points lead', 'new points leader {l}', '{l} grabs the points lead'],
  eliminated: ['{x} out of the playoff', '{x} knocked out of the title hunt'],
  eliminatedMany: ['{n} drivers knocked out of the playoff', 'the playoff loses {n}'],
  leadToo: ['and the points lead comes with it', 'and he takes over the points lead'],
  boiledHis: ['and his feud with {o} boils over', 'and the bad blood with {o} runs hotter'],
  boiled: ['{a}–{b} boils over', '{a} and {b} at it again', 'bad blood between {a} and {b}'],
  hurt: ['{h} out {n} races', '{h} hurt, out {n}', '{h} sidelined for {n}'],
  hurtSeason: ['{h} out for the year', 'season over for {h}'],
  mourn: ['The league mourns {d}', '{d} lost at {t}'],
} as const;

function headline(w: WeekContext, rivals: readonly WireRivalry[]): { headline: WireLine; standfirst: WireLine | null } {
  const winnerId = w.run.race.winnerId;
  const key = w.key;
  const W = winnerId ? w.short(winnerId) : null;
  const ids = winnerId ? [winnerId] : [];
  let main: WireLine | null = null;
  if (W && winnerId) {
    const career = w.people.streaks[winnerId];
    let streak = 0;
    for (let i = w.races.length - 1; i >= 0 && w.races[i].race.winnerId === winnerId; i--) streak++;
    const start = w.run.startPositions[winnerId];
    // His first win anywhere: one league win and no pre-league wins (a 2025 driver's priors count).
    const first = career?.wins === 1 && !(w.priors[winnerId]?.wins ?? 0);
    if (streak >= 2) main = say('head-streak', HEAD.streak, { w: W, n: num(streak, 'word'), t: w.trackShort }, { key, driverIds: ids });
    else if (first) main = say('head-first', HEAD.first, { w: W, t: w.trackShort }, { key, driverIds: ids });
    else if (start !== undefined && start >= 20) main = say('head-deep', HEAD.deep, { w: W, t: w.trackShort, start: num(start, 'ord') }, { key, driverIds: ids });
    else main = say('head-won', HEAD.won, { w: W, t: w.trackShort }, { key, driverIds: ids });
  }

  // The second story, in order of weight.
  let second: WireLine | null = null;
  const died = w.out.died[0];
  if (died) {
    second = say('head-mourn', HEAD.mourn, { d: died.name, t: w.trackShort }, { key, driverIds: [died.driverId] });
    // A death leads the paper.
    if (second && main) [main, second] = [second, main];
  }
  if (!second) {
    const playoffOut = w.season.season.playoff?.rounds?.find((r) => r.races[r.races.length - 1] === w.raceNumber);
    if (playoffOut && playoffOut.eliminated.length > 0) {
      second =
        playoffOut.eliminated.length === 1
          ? say('head-eliminated', HEAD.eliminated, { x: w.short(playoffOut.eliminated[0]) }, { key, driverIds: playoffOut.eliminated })
          : say('head-eliminated-many', HEAD.eliminatedMany, { n: num(playoffOut.eliminated.length, 'word') }, { key, driverIds: playoffOut.eliminated });
    }
  }
  if (!second && w.prevTable) {
    const was = w.prevTable.drivers[0]?.driverId;
    const now = w.table.drivers[0]?.driverId;
    if (was && now && was !== now)
      second =
        now === winnerId
          ? say('head-lead-too', HEAD.leadToo, {}, { key, driverIds: [now] })
          : say('head-lead-change', HEAD.leadChange, { l: w.short(now) }, { key, driverIds: [now] });
  }
  if (!second) {
    const boil = rivals.find((r) => (r.change === 'hotter' || r.change === 'new') && (r.state === 'boiling' || r.state === 'hot'));
    if (boil) {
      const mine = boil.a === winnerId || boil.b === winnerId;
      const other = boil.a === winnerId ? boil.b : boil.a;
      second = mine
        ? say('head-boiled-his', HEAD.boiledHis, { o: w.short(other) }, { key, driverIds: [other] })
        : say('head-boiled', HEAD.boiled, { a: w.short(boil.a), b: w.short(boil.b) }, { key, driverIds: [boil.a, boil.b] });
    }
  }
  if (!second) {
    const hurt = w.out.out.find((o) => o.isNew && o.kind === 'injured');
    if (hurt) {
      second =
        hurt.racesLeft === 'season'
          ? say('head-hurt-season', HEAD.hurtSeason, { h: w.short(hurt.driverId) }, { key, driverIds: [hurt.driverId] })
          : hurt.racesLeft >= 2
            ? say('head-hurt', HEAD.hurt, { h: w.short(hurt.driverId), n: num(hurt.racesLeft, 'word') }, { key, driverIds: [hurt.driverId] })
            : null;
    }
  }
  const head = main && second ? join(main, second, second.kind === 'head-lead-too' || second.kind === 'head-boiled-his' ? ', ' : '; ') : (main ?? second ?? say('head-none', ['The week in the league'], {}, { key })!);

  // The standfirst: the podium, then the title picture.
  const order = w.run.race.finishingOrder;
  const p2 = order.find((f) => f.place === 2);
  const p3 = order.find((f) => f.place === 3);
  const lead = w.table.drivers[0];
  const runner = w.table.drivers[1];
  const pic = w.picture;
  const podium = [p2?.driverId, p3?.driverId].filter((x): x is string => Boolean(x));
  const left = w.schedule.length - w.raceNumber;
  let stand: WireLine | null = null;
  if (pic && pic.phase === 'playoff') {
    const nextTrack = w.next ? shortTrack(w.next.trackName) : null;
    if (pic.round.round === 'championship') {
      const four = pic.order.slice(0, 4);
      stand = say(
        'standfirst-final',
        ['{p2} second, {p3} third. Four are left for the title at {next}: {f1}, {f2}, {f3} and {f4}. Best finish of the four takes it.'],
        { p2: p2 ? w.short(p2.driverId) : null, p3: p3 ? w.short(p3.driverId) : null, next: nextTrack, f1: four[0] ? w.short(four[0]) : null, f2: four[1] ? w.short(four[1]) : null, f3: four[2] ? w.short(four[2]) : null, f4: four[3] ? w.short(four[3]) : null },
        { key, driverIds: [...podium, ...four] },
      );
    } else if (pic.freshRound) {
      stand = say(
        'standfirst-round',
        [
          '{p2} second, {p3} third. The {round} opens at {next}: {alive} drivers, round points back to zero, {adv} move on.',
          'Behind him, {p2} and {p3}. Now the {round}: {alive} left, {adv} advance after three races, starting at {next}.',
        ],
        { p2: p2 ? w.short(p2.driverId) : null, p3: p3 ? w.short(p3.driverId) : null, round: pic.round.label, next: nextTrack, alive: num(pic.inIds.size, 'word'), adv: num(pic.round.advancing ?? 1, 'word') },
        { key, driverIds: podium },
      );
    } else {
      const top = pic.order[0];
      const cutId = pic.cut?.driverId ?? null;
      stand = say(
        'standfirst-playoff',
        [
          '{p2} second, {p3} third. In the {round}, {top} leads on round points; {cut} holds the last spot in with {togo} {togo|race|races} to go.',
          'Behind him, {p2} and {p3}. {top} tops the {round}; {cut} is on the cut line, {togo} {togo|race|races} left in the round.',
        ],
        { p2: p2 ? w.short(p2.driverId) : null, p3: p3 ? w.short(p3.driverId) : null, round: pic.round.label, top: top ? w.short(top) : null, cut: cutId ? w.short(cutId) : null, togo: num(pic.racesToCut, 'word') },
        { key, driverIds: [...podium, ...(top ? [top] : []), ...(cutId ? [cutId] : [])] },
      );
    }
  } else {
    const cutId = pic?.cut?.driverId ?? null;
    stand = say(
      'standfirst',
      [
        '{p2} ran second and {p3} third. {leader} leads the points by {gap} with {left} {left|race|races} to go.',
        'Behind him, {p2} and {p3}. {leader} is {gap} clear at the top with {left} {left|race|races} left.',
        '{p2} second, {p3} third; {leader} holds a {gap}-point lead, {left} {left|race|races} to run.',
        ...(w.raceNumber >= 10 ? ['{p2} second, {p3} third. {cut} holds the last playoff spot on points, {tocut} {tocut|race|races} before the cut.'] : []),
      ],
      {
        p2: p2 ? w.short(p2.driverId) : null,
        p3: p3 ? w.short(p3.driverId) : null,
        leader: lead ? w.short(lead.driverId) : null,
        gap: lead && runner && lead.points > runner.points ? num(lead.points - runner.points) : null,
        left: num(left),
        cut: cutId ? w.short(cutId) : null,
        tocut: pic ? num(pic.racesToCut, 'word') : null,
      },
      { key, driverIds: [...podium, ...(lead ? [lead.driverId] : []), ...(cutId ? [cutId] : [])] },
    );
  }
  return { headline: head, standfirst: stand };
}

function join(a: WireLine, b: WireLine, sep: string): WireLine {
  // After a semicolon a clause starts small — unless it starts with a name.
  const second = b.template.startsWith('{') ? b.text : b.text.charAt(0).toLowerCase() + b.text.slice(1);
  return {
    kind: `${a.kind}+${b.kind}`,
    variant: a.variant * 10 + b.variant,
    template: `${a.template}${sep}${b.template}`,
    text: `${a.text}${sep}${second}`,
    names: { ...prefix(a.names, 'a_'), ...prefix(b.names, 'b_') },
    nums: { ...prefix(a.nums, 'a_'), ...prefix(b.nums, 'b_') },
    driverIds: [...a.driverIds, ...b.driverIds],
  };
}

function prefix<T>(o: Record<string, T>, p: string): Record<string, T> {
  return Object.fromEntries(Object.entries(o).map(([k, v]) => [p + k, v]));
}

// ---------------------------------------------------------------------------
// The Wire
// ---------------------------------------------------------------------------

/**
 * The Wire for the week after race `raceNumber` of season `seasonNumber`, or
 * null when that race has not been run or was the season's last.
 */
export function buildWire(league: WireLeague, seasonNumber: number, raceNumber: number): Wire | null {
  const w = weekContext(league, seasonNumber, raceNumber);
  if (!w || !w.next) return null;

  const rivals = rivalryWatch(w);
  const { headline: head, standfirst } = headline(w, rivals);

  // Power rankings: one forward pass through the season, so movement and the
  // variety of reasons are read off the weeks before, never after.
  const weeks = powerWeeks(w.season, raceNumber, {
    key: w.people.leagueKey,
    lostAfter: (n) => {
      const rep = outAfterRace(w.season, n);
      const gone = new Set<string>(rep.died.map((d) => d.driverId));
      for (const s of w.seasons) for (const f of s.season.fatalities ?? []) if (s.seasonNumber < seasonNumber || f.raceNumber <= n) gone.add(f.driverId);
      for (const o of rep.out) if (o.racesLeft === 'season') gone.add(o.driverId);
      return gone;
    },
  });
  const power = weeks[weeks.length - 1]?.raceNumber === raceNumber ? weeks[weeks.length - 1].rows : [];

  // The line: everyone on the season's books after race N who can race next week.
  const seen = new Set<string>();
  const field: { driverId: string; name: string }[] = [];
  const consider = (id: string) => {
    if (seen.has(id) || w.unavailable.has(id)) return;
    seen.add(id);
    field.push({ driverId: id, name: w.nameOf(id) });
  };
  for (const f of w.run.race.finishingOrder) consider(f.driverId);
  for (const d of w.table.drivers) if (d.entries > 0) consider(d.driverId);
  for (const r of (w.season.season.rookies ?? []) as readonly { driverId: string; raceNumber?: number }[]) {
    if (r.raceNumber === undefined || r.raceNumber <= raceNumber) consider(r.driverId);
  }
  const entries = oddsFor(w.seasons, seasonNumber, w.next, field);
  const shown = entries.slice(0, ODDS_SHOWN);
  const odds = entries.length
    ? {
        entries: shown.map((e) => ({ driverId: e.driverId, name: e.name, line: e.line, chance: e.chance, note: oddsNote(w, e) })),
        field: fieldLine(entries, ODDS_SHOWN),
        favourite: shown[0]?.driverId ?? null,
      }
    : null;

  return {
    key: `wire|${w.key}`,
    seasonNumber,
    afterRace: raceNumber,
    raceName: w.run.scheduled.raceName,
    track: w.track,
    next: {
      raceNumber: w.next.raceNumber,
      raceName: w.next.raceName,
      track: trackInProse(w.next.trackName),
      turns: w.next.turns,
      doublePoints: w.next.doublePoints,
      round: w.picture?.round.label ?? 'Regular Season',
    },
    headline: head,
    standfirst,
    quotes: garageQuotes(w),
    power,
    odds,
    injuries: injuryLines(w),
    sponsors: sponsorDesk(w),
    rivalries: rivals,
  };
}

/** Every line the Wire printed, for the measures and the grounding test. */
export function wireLines(wire: Wire): WireLine[] {
  return [
    wire.headline,
    ...(wire.standfirst ? [wire.standfirst] : []),
    ...wire.quotes.map((q) => q.context),
    ...wire.power.map((p) => p.reason),
    ...(wire.odds?.entries.map((e) => e.note).filter((n): n is WireLine => n !== null) ?? []),
    ...wire.injuries,
    ...wire.sponsors,
    ...wire.rivalries.flatMap((r) => [...(r.badge ? [r.badge] : []), ...r.lines]),
  ];
}
